import axios from 'axios'
import SchedulingServiceClient from '@/clients/SchedulingServiceClient'
import UnifiedSchedulingClient from '@/clients/UnifiedSchedulingClient'
import {
  FALLBACK_WIDGET_VERSION,
  MODE_PARAM_MAPPING,
  ORIGIN_CARE_CONNECT,
  ORIGIN_PRACTICE_KAREO,
  STORAGE_CONFIG_ITEM_NAME,
  WIDGET_VERSIONS
} from '@loader-src/constants'
import PracticeMapping from '@/entities/PracticeMapping/PracticeMapping'
import LocationMapping from '@/entities/LocationMapping/LocationMapping'
import ProviderMapping from '@/entities/ProviderMapping/ProviderMapping'
import FeatureSetting from '@/entities/FeatureSetting/FeatureSetting'

const env = window.Cypress ? window.Cypress.env() : process.env

class SchedulingWrapperWidgetLoader {
  constructor (config = {}) {
    this.baseConfig = config
    this.widgetVersion = FALLBACK_WIDGET_VERSION
    this.widgetURL = this.getWidgetURL(this.widgetVersion)
    this.widgetOrigin = (new URL(this.getWidgetURL(this.widgetVersion))).origin
    this.rawWidgetHtml = ''
    this.processedWidgetHtml = ''
    this.configForCurrentWidget = this.createConfigWidgetSpecificConfig(this.baseConfig, this.widgetVersion)
    this.runWidget()
  }

  async runWidget () {
    this.widgetVersion = await this.checkWidgetVersion(this.baseConfig.practiceUuid)
    this.widgetURL = this.getWidgetURL(this.widgetVersion)
    this.widgetOrigin = (new URL(this.widgetURL)).origin
    this.rawWidgetHtml = await this.loadWidgetCodeByUrl(this.widgetURL)
    this.processedWidgetHtml = this.processedWidgetHTML(this.rawWidgetHtml, this.widgetOrigin)
    this.configForCurrentWidget = this.createConfigWidgetSpecificConfig(this.baseConfig, this.widgetVersion)
    this.passConfigToWidget(this.configForCurrentWidget)
    this.showWidget(this.processedWidgetHtml)
  }

  /**
   * Get the PO Book Online Settings
   * @param {string} practiceUuid
   * @param {number} locationId
   * @param {number} providerId
   * @param {PracticeMapping} practiceMapping
   * @return {Promise<void>}
   */
  async getPoBookOnlineSettings ({ practiceUuid, locationId, providerId, practiceMapping }) {
    const unifiedSchedulingClient = new UnifiedSchedulingClient()

    // set up variables to store info from API calls
    let locationMappingData = {}
    let providerMappingData = {}

    // get mapping of location from PG to PO
    const getLocationMapping = () => {
      return unifiedSchedulingClient.getLocationMapping(practiceUuid, locationId)
        .then((response) => {
          locationMappingData = new LocationMapping(response.data)
        })
    }

    // get mapping of provider from PG to PO
    const getProviderMapping = () => {
      return unifiedSchedulingClient.getProviderMapping(practiceUuid, providerId)
        .then((response) => {
          providerMappingData = new ProviderMapping(response.data)
        })
    }

    // set up the API calls
    const apiCalls = []

    // if there is location, then get location mapping
    if (locationId) {
      apiCalls.push(getLocationMapping())
    }

    // if there is provider, then get provider mapping
    if (providerId) {
      apiCalls.push(getProviderMapping())
    }

    await Promise.allSettled(apiCalls)

    // set new Kareo practiceKey to base config
    this.baseConfig.practiceKey = practiceMapping.practiceKey

    // update the url params with new Kareo practiceKey
    const url = new URL(window.location.href)
    url.searchParams.set('practiceKey', practiceMapping.practiceKey)

    // delete practiceUuid from URL since PO does not use it
    url.searchParams.delete('practiceUuid')

    // if there is a mapped location then use that
    const mappedLocationId = locationMappingData.poLocationId
    if (mappedLocationId) {
      this.baseConfig.locationId = mappedLocationId
      url.searchParams.set('locationId', mappedLocationId)
    }

    // if there is a mapped provider then use that
    const mappedProviderId = providerMappingData.poProviderId
    if (mappedProviderId) {
      this.baseConfig.providerId = mappedProviderId
      url.searchParams.set('providerId', mappedProviderId)
    }

    // update history state
    window.history.pushState(null, '', url.toString())
  }

  async checkWidgetVersion (practiceUuid) {
    // if practiceKey exists && it's for PO - use unified scheduling widget
    if (Boolean(this.baseConfig.practiceKey) && String(this.baseConfig.practiceKey).startsWith('k')) {
      return WIDGET_VERSIONS.BOOK_ONLINE_UNIFIED
    }

    // use variables for storing information from API calls
    let widgetVersion = null
    let practiceMapping = {}

    const schedulingService = new SchedulingServiceClient()
    const unifiedSchedulingClient = new UnifiedSchedulingClient()

    // get value from feature set to determine which widget to load
    const getWidgetVersion = () => {
      return schedulingService.getSchedulingVersion(practiceUuid)
        .then((response) => {
          const featureSetting = new FeatureSetting(response.data)
          widgetVersion = featureSetting.value
        })
    }

    // check to see if there is any mapping of practice from PG to PO
    const getPracticeMapping = () => {
      return unifiedSchedulingClient.getPracticeMapping(practiceUuid)
        .then((response) => {
          practiceMapping = new PracticeMapping({ ...response.data, practiceUuid })
        })
    }

    // wait till all API calls have finished (using settled as some may return error which is expected)
    await Promise.allSettled([getWidgetVersion(), getPracticeMapping()])

    // if there is a mapping of practice, then get PO online book settings
    if (practiceMapping.enabled) {
      await this.getPoBookOnlineSettings({ ...this.baseConfig, practiceMapping })
    }

    return WIDGET_VERSIONS[widgetVersion] ?? FALLBACK_WIDGET_VERSION
  }

  getWidgetURL (version) {
    switch (version) {
      case WIDGET_VERSIONS.BOOK_ONLINE_UNIFIED:
        return env.VUE_APP_UNIFIED_SCHEDULING_URL
      case WIDGET_VERSIONS.BOOK_ONLINE_V4:
        return env.VUE_APP_BOOK_ONLINE_V4_URL
      case WIDGET_VERSIONS.APPOINTMENT_REQUEST:
        return env.VUE_APP_BOOK_ONLINE_V3_URL
      case WIDGET_VERSIONS.BOOK_ONLINE_V2:
        return env.VUE_APP_BOOK_ONLINE_V2_URL
      default:
        return env.VUE_APP_BOOK_ONLINE_V3_URL
    }
  }

  async loadWidgetCodeByUrl (url) {
    return await axios.get(url).then(response => {
      return response.data
    }).catch((e) => {
      return ''
    })
  }

  processedWidgetHTML (html, origin) {
    const doc = document.implementation.createHTMLDocument('')
    doc.open()
    doc.write(html)
    doc.close()
    const baseTag = doc.createElement('base')
    baseTag.href = origin
    doc.head.appendChild(baseTag)
    const allScriptAndStyleNodes = doc.documentElement.querySelectorAll('link, script')
    allScriptAndStyleNodes.forEach((item) => {
      const propName = item.src ? 'src' : 'href'
      if (item[propName]) {
        const a = doc.createElement('a')
        a.href = item[propName]
        let pathName = a.href.indexOf('http') < 0 ? a.href : a.pathname
        pathName = pathName.indexOf('/') !== 0 ? `/${pathName}` : pathName
        item[propName] = `${origin}${pathName}`
      }
    })
    return `${doc.head.outerHTML}${doc.body.outerHTML}`
  }

  createConfigWidgetSpecificConfig (baseConf = {}, version = FALLBACK_WIDGET_VERSION) {
    // all of this parameters are common for all widgets
    const specificConfig = {
      practiceId: (baseConf.practiceId ?? null),
      practiceUuid: (baseConf.practiceUuid ?? null),
      originHost: (baseConf.originHost ?? ''),
      showCloseButton: baseConf.showCloseButton ? 1 : 0,
      gaId: (baseConf.gaId ?? null)
    }

    // add origin only if exist
    if (baseConf.origin) {
      specificConfig.origin = baseConf.origin
    }

    if (WIDGET_VERSIONS.BOOK_ONLINE_UNIFIED === version) {
      specificConfig.practiceKey = baseConf.practiceKey
      specificConfig.showCloseButton = (specificConfig?.origin === ORIGIN_CARE_CONNECT || specificConfig?.origin === ORIGIN_PRACTICE_KAREO) ? 0 : 1 // Do not display widget close button on CareConnect & Practice.kareo4
    }

    if (MODE_PARAM_MAPPING[version]) {
      specificConfig.mode = MODE_PARAM_MAPPING[version]
    }
    if ([WIDGET_VERSIONS.BOOK_ONLINE_V4, WIDGET_VERSIONS.BOOK_ONLINE_UNIFIED].includes(version)) {
      specificConfig.useWidgetManager = !baseConf.notWidgetManagerUsage
      if (baseConf.locationId) {
        specificConfig.locationId = baseConf.locationId
      }
      if (baseConf.providerId) {
        specificConfig.providerId = baseConf.providerId
      }
      if (baseConf.appointmentTypeId) {
        specificConfig.appointmentTypeId = baseConf.appointmentTypeId
      }
    }

    return specificConfig
  }

  passConfigToWidget (config = {}) {
    localStorage.setItem(STORAGE_CONFIG_ITEM_NAME, JSON.stringify(config))
  }

  showWidget (html) {
    if (!html) {
      return
    }
    document.open()
    document.write(html)
    document.close()
  }
}

let instanse
export default (config) => {
  if (!instanse) {
    instanse = new SchedulingWrapperWidgetLoader(config)
    // later can be used from widgets
    window.SCHEDULING_WRAPPER_WIDGET_LOADER = instanse
  }
  return instanse
}
